import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SectionHeading from '@/components/feature/SectionHeading';
import Reveal from '@/components/feature/Reveal';
import { useSeo } from '@/hooks/useSeo';
import { testimonials } from '@/mocks/testimonials';

const positions = [
  { icon: 'ri-compass-3-line', label: '定位', value: '轻量围观资讯站', text: '不追求大而全，只把当天值得看的内容整理清楚。' },
  { icon: 'ri-group-line', label: '面向', value: '喜欢蹲点的群众', text: '想跟上节奏、又不想被情绪带着走的人。' },
  { icon: 'ri-bookmark-3-line', label: '风格', value: '克制 · 好读 · 可验证', text: '只写看到的，把判断权留给你自己。' },
];

const principles = [
  { title: '来源清楚', text: '每一条记录都会标注时间、地点和来源类型，方便你判断可信度。' },
  { title: '不替人下结论', text: '没有定论的话题，我们只摆事实和时间线，不给情绪化的判断。' },
  { title: '可以更正', text: '记录有误会改，当事人提出合理异议会下架，我们更在意内容靠不靠谱。' },
  { title: '保持节奏', text: '不追无意义的更新量，宁可少发一条，也不发一条没用的。' },
];

const milestones = [
  { year: '起点', text: '因为总在群里重复解释同一件事，干脆把现场见闻整理成可分享的记录。' },
  { year: '成形', text: '几位常跑现场的朋友加入，蹲点记录开始有了固定的时间和地点标注。' },
  { year: '现在', text: '内容分成五个主题，热榜话题与现场记录并行，节奏稳定了下来。' },
  { year: '往后', text: '继续把细节做扎实，让每个要去现场的人都能少走一点弯路。' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 511群众围观阵地的定位与内容原则',
    description:
      '了解 511群众：一个抱团吃瓜的围观资讯站。这里说明我们的网站定位、发展理念、内容原则与联系投稿方式，也分享围观群众给我们的真实反馈。',
    keywords: '511群众,511群众是什么,511群众内容原则,511群众抱团围观,吃瓜小队介绍',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 511群众',
        description: '介绍 511群众的网站定位、发展理念与内容原则。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '511群众',
        url: 'https://511qunzhong.space/',
        email: 'hello@511qunzhong.space',
        description: '面向吃瓜围观人群的轻资讯聚合与蹲点记录站点。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://511qunzhong.space/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://511qunzhong.space/about' },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 lg:px-12 pt-10 md:pt-14 pb-4">
        <Breadcrumbs items={[{ label: '关于我们' }]} />
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs md:text-sm tracking-[0.18em] uppercase text-primary-700">About us</p>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-5xl leading-tight text-foreground-950">
              一个把瓜吃明白的
              <br />
              围观阵地
            </h1>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-700">
              511群众始于一个很朴素的念头：与其在群里反复解释同一件事，不如把公开可见的信息整理成一份清晰、可分享的记录。于是有了吃瓜小队，也有了这块专门用来抱团围观的地方。
            </p>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              我们不生产话题，也不评判谁对谁错。我们做的，是把散落各处的现场见闻和热榜脉络收拢到一处，让你花最少的时间跟上节奏。
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/frontline"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-radar-line"></i>
                去围观前线看看
              </Link>
              <Link
                to="/squad"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md border border-background-300 text-foreground-800 font-medium hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-team-line"></i>
                认识吃瓜小队
              </Link>
            </div>
          </div>

          <Reveal>
            <div className="w-full h-72 md:h-[440px] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20artistic%20illustration%20of%20a%20cozy%20shared%20reading%20corner%20with%20notebooks%20and%20a%20small%20lantern%2C%20soft%20amber%20and%20terracotta%20tones%2C%20cream%20walls%2C%20minimal%20editorial%20style%2C%20gentle%20texture%20and%20grain&width=1200&height=900&seq=511-about-main&orientation=landscape"
                alt="511群众内容整理场景暖色调插画"
                title="关于 511群众 · 抱团吃瓜围观阵地"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100">
        <SectionHeading eyebrow="Positioning" title="我们把自己放在什么位置" />
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {positions.map((item, index) => (
            <Reveal key={item.label} delay={index * 90}>
              <div className="h-full rounded-lg bg-background-50 border border-background-200/80 p-5 md:p-6">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 text-xl">
                  <i className={item.icon}></i>
                </span>
                <p className="mt-4 text-xs tracking-widest text-foreground-500 uppercase">{item.label}</p>
                <h3 className="mt-1 font-heading font-bold text-lg text-foreground-950">{item.value}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16">
          <SectionHeading
            eyebrow="Principles"
            title="内容原则"
            description="这几条不是口号，是我们每发一条记录前都会过一遍的检查项。"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {principles.map((item, index) => (
              <Reveal key={item.title} delay={index * 80}>
                <div className="h-full rounded-lg border-l-2 border-primary-400 bg-background-100/70 p-4 md:p-5">
                  <h3 className="font-heading font-semibold text-base text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-foreground-950 text-background-100">
        <SectionHeading eyebrow="Our path" title="一路走来" invert />
        <ol className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-5">
          {milestones.map((item) => (
            <li key={item.year} className="relative pt-6 border-t border-background-800">
              <span className="absolute -top-[7px] left-0 w-3.5 h-3.5 rounded-full bg-accent-500"></span>
              <p className="font-heading font-bold text-accent-400">{item.year}</p>
              <p className="mt-2 text-sm leading-relaxed text-background-300">{item.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <SectionHeading eyebrow="Voices" title="用户口碑" description="这些话来自常来逛的群众，也是我们继续做的理由。" />
        <div className="mt-8 divide-y divide-background-200/80 border-y border-background-200/80">
          {testimonials.map((item) => (
            <div key={item.id} className="py-6 flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
              <div className="flex items-center gap-3 md:w-52 shrink-0">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-500 text-secondary-950 font-heading font-bold">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                  <span className="block text-xs text-foreground-500">{item.tag}</span>
                </span>
              </div>
              <p className="text-sm md:text-base leading-relaxed text-foreground-700">{item.content}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <SectionHeading
            eyebrow="Contact"
            title="联系我们"
            description="投稿、纠错、合作，都可以直接写信给我们。我们会认真读每一条留言，并在整理时参考你的信息。"
          />
          <div className="rounded-lg bg-background-50 border border-background-200/80 p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-xl">
                <i className="ri-mail-send-line"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">投稿与联系邮箱</p>
                <a
                  href="mailto:hello@511qunzhong.space"
                  rel="nofollow"
                  className="font-heading font-bold text-lg text-foreground-950 hover:text-primary-700 transition-colors"
                >
                  hello@511qunzhong.space
                </a>
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600">
              如果你更习惯直接留言，也可以回到首页底部的留言区，填好时间和地点发给我们。
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-chat-3-line"></i>
              回首页留言区
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}